import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { usePathname } from 'src/routes/hooks';
import ErrorState from './error';

interface BoundaryProps {
  children: ReactNode;
  /** A caught error clears when this value changes, so navigating away recovers the page. */
  resetKey: string;
}

interface BoundaryState {
  error: Error | null;
  resetKey: string;
}

class Boundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error: Error): Partial<BoundaryState> {
    return { error };
  }

  // Runs on every render, including the one that follows a caught error. Comparing against the
  // key stored in state (not the previous props) keeps an error thrown by the new route from
  // being cleared by the same navigation that rendered it.
  static getDerivedStateFromProps(
    props: BoundaryProps,
    state: BoundaryState
  ): Partial<BoundaryState> | null {
    if (props.resetKey === state.resetKey) return null;
    return { error: null, resetKey: props.resetKey };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // React hands caught render errors to the boundary instead of window.onerror, so Sentry's
    // global handler never sees them.
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
    });
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    const { children } = this.props;

    if (error) {
      return (
        <ErrorState
          error={error}
          message="This page hit an unexpected problem. Try again, or reload if it keeps happening."
          onRetry={this.reset}
        />
      );
    }

    return children;
  }
}

/**
 * Catches errors thrown while rendering `children` and shows the shared Error state in their
 * place, so the surrounding shell (sidebar, navigation) stays usable. Clears on pathname change.
 */
export default function ErrorBoundary({ children }: { children: ReactNode }) {
  const pathname = usePathname();

  return <Boundary resetKey={pathname}>{children}</Boundary>;
}
